/**
 * voiceState — the PURE derivation behind the visible voice state machine, shared by
 * EVERY renderer of the machine (lane va-ui-composer-fsm-2026-10-01, issue #2204).
 *
 * Extracted from desktop/heroVoiceState.ts (lane va-ui-desktop-home-2026-10-01, issue
 * #2201) so the home hero AND the docked composer consume the SAME derivation — never a
 * parallel copy (issue #2204: "rendering the SAME machine via the stores"). The desktop
 * module re-exports these names under its hero-specific aliases; nothing else about the
 * hero contract changes.
 *
 * Each INPUT is an EXISTING source of truth — this module adds no state of its own
 * (docs/frontend-fsm-pattern.md: components render the machine, never own it):
 *
 *   listening  ← composerStore.globalRecording — the single global mic slot the recorder
 *                writes at start/stop. `startedAt != null` means a recording is live.
 *   speaking   ← the shared TTS api (useTtsShared) `speaking` state — the same flag the
 *                per-message read-aloud buttons and streaming auto-read render.
 *   processing ← the composer outbound FSM's busy set (any session mid-turn).
 *
 * Precedence: listening > speaking > processing > idle — a held mic is the strongest
 * signal; an actively speaking channel beats a grinding pipeline; otherwise a busy
 * session renders 'processing'; nothing live renders 'idle'.
 *
 * SE-pinned: setpoint-evals/ui-desktop-home/SE-01 (hero states + ring distinctness),
 * SE-05 (this set is exhaustive — a fifth state is a defect); setpoint-evals/
 * ui-composer-fsm/SE-01 (the SAME contract on the composer mic), SE-04 (exhaustive set).
 */

export const VOICE_STATES = ['idle', 'listening', 'processing', 'speaking'] as const;
export type VoiceState = (typeof VOICE_STATES)[number];

export interface VoiceStateInputs {
  /** A recording holds the global mic slot (composerStore.globalRecording.startedAt != null). */
  recording: boolean;
  /** The shared TTS channel is actively speaking. */
  speaking: boolean;
  /** Any session is mid-turn (the composer busy set is non-empty). */
  processing: boolean;
}

export function resolveVoiceState(input: VoiceStateInputs): VoiceState {
  if (input.recording) return 'listening';
  if (input.speaking) return 'speaking';
  if (input.processing) return 'processing';
  return 'idle';
}

/** State label rendered next to the machine's controls (role="status" aria-live="polite"). */
export function voiceStateLabel(state: VoiceState): string {
  switch (state) {
    case 'listening':
      return 'Listening…';
    case 'processing':
      return 'Thinking…';
    case 'speaking':
      return 'Speaking…';
    default:
      return 'Ready';
  }
}
